import { useQuery } from '@tanstack/react-query';
import { DataTable, Badge, EmptyState } from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { apiClient } from '@/lib/api-client';

// ─── Design tokens ─────────��────────────────────────────────────────────────
const GLASS_PANEL =
  'rounded-2xl bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)]';

interface AuditEntry {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string;
  user_name?: string;
  user_email?: string;
  created_at: string;
}

interface AuditListResponse {
  data: AuditEntry[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

const ACTION_VARIANT: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
  CREATE: 'success',
  UPDATE: 'info',
  DELETE: 'danger',
  STATUS_CHANGE: 'warning',
};

const columns: ColumnDef<AuditEntry>[] = [
  {
    key: 'created_at',
    header: 'Time',
    render: (v) => (
      <span className="tabular-nums text-xs text-neutral-500 dark:text-neutral-400">
        {new Date(v as string).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </span>
    ),
  },
  {
    key: 'action',
    header: 'Action',
    render: (v) => (
      <Badge variant={ACTION_VARIANT[v as string] ?? 'info'}>
        {(v as string).replace(/_/g, ' ')}
      </Badge>
    ),
  },
  { key: 'entity_type', header: 'Entity' },
  {
    key: 'user_name',
    header: 'By',
    render: (v, row) => (
      <span className="text-sm text-neutral-700 dark:text-neutral-300">
        {(v as string) ?? row.user_email ?? '—'}
      </span>
    ),
  },
];

interface TenantAuditTabProps {
  tenantId: string;
}

export function TenantAuditTab({ tenantId }: TenantAuditTabProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['tenant', tenantId, 'audit'],
    queryFn: () => apiClient<AuditListResponse>(`/audit?tenant_id=${tenantId}&limit=50`),
  });

  const entries = data?.data ?? [];

  if (!isLoading && entries.length === 0) {
    return (
      <div className="animate-slide-up">
        <EmptyState
          variant="page"
          title="No audit entries"
          description="Actions performed on this tenant will appear here"
        />
      </div>
    );
  }

  return (
    <div className={`${GLASS_PANEL} overflow-hidden animate-slide-up transition-shadow duration-200 hover:shadow-lg`}>
      <DataTable
        columns={columns}
        data={entries}
        loading={isLoading}
        variant="compact"
        emptyState={{ title: 'No audit entries', description: 'No audit trail yet' }}
      />
    </div>
  );
}
